import React from 'react';
import { useShallow } from 'zustand/react/shallow';

import { EmptyStateIcon } from '../rosettaSvgs';
import { HighlightedText } from './TradeScopeComponents';
import { Lozenge, Divider } from '@attentive-platform/stem-ui';
import { SCOPE_TYPE_ENUM } from '../../../Constants/Constants';
import { useRosettaStore } from '../../../Stores/useRosettaStore';

const ScopeDetailsPanel = () => {
  const { currentDetailItem, currentImages, selectedTrades, tradesData, searchQuery, dispatch } = useRosettaStore(
    useShallow(state => ({
      currentDetailItem: state.currentDetailItem,
      currentImages: state.currentImages,
      selectedTrades: state.selectedTrades,
      tradesData: state.tradesData,
      searchQuery: state.searchQuery,
      dispatch: state.dispatch
    }))
  );

  const openImageModal = index => {
    dispatch({ type: 'SET_CURRENT_IMAGE_INDEX', payload: index });
    dispatch({ type: 'SET_SHOW_IMAGE_MODAL', payload: true });
  };

  return (
    <section className='rosetta-panel'>
      <div className='rosetta-panel-header'>
        <h2>Scope Details</h2>
        <span className='helper-text'>Click on any scope item to view details</span>
      </div>
      <div className='rosetta-panel-content'>
        {currentDetailItem ? (
          <div className='rosetta-details-container'>
            <div className='rosetta-details-header'>
              <h3 className='rosetta-details-title'>
                <HighlightedText text={currentDetailItem.scope_name} searchQuery={searchQuery} />
              </h3>
              <Lozenge
                color={currentDetailItem.scope_type === SCOPE_TYPE_ENUM.BASIC ? 'green' : 'orange'}
                variant={currentDetailItem.scope_type === SCOPE_TYPE_ENUM.BASIC ? 'success' : 'warning'}
                label={currentDetailItem.scope_type === SCOPE_TYPE_ENUM.BASIC ? 'BASIC' : 'ADVANCED'}
                size='medium'
                className='my-2'
              />
              <p className='rosetta-details-description'>
                <HighlightedText text={currentDetailItem.scope_description || ''} searchQuery={searchQuery} />
              </p>
              {currentDetailItem?.scope_link && (
                <div className='rosetta-details-description'>
                  <span className='mr-2'>Example:</span>
                  <a href={currentDetailItem?.scope_link} target='_blank' rel='noopener noreferrer'>
                    {''}
                    {currentDetailItem?.scope_link}
                  </a>
                </div>
              )}
            </div>

            {currentImages.length > 0 && (
              <>
                <Divider />
                <div className='rosetta-visual-examples-section'>
                  <h4 className='rosetta-visual-examples-title'>Visual Examples & Deliverables</h4>
                  <div className='rosetta-image-gallery'>
                    {currentImages.map((imageUrl, index) => (
                      <div key={index} className='rosetta-image-item' onClick={() => openImageModal(index)}>
                        <img src={imageUrl} alt={`${index + 1}`} />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className='rosetta-empty-state'>
            <EmptyStateIcon />
            <h3>Select a scope item to view details</h3>
            <p>Click any scope item in the left panel to see comprehensive details and visual examples.</p>
          </div>
        )}

        {/* Standard Assumptions & Exclusions */}
        {selectedTrades.size > 0 && (
          <>
            <Divider />
            <div className='rosetta-trade-notes-section'>
              <h4 className='rosetta-trade-notes-title'>Standard Assumptions & Exclusions</h4>
              <div className='rosetta-trade-notes-content'>
                {Array.from(selectedTrades).map(tradeId => {
                  const trade = tradesData.find(t => t.trade_id === tradeId);
                  return trade && trade.trade_description ? (
                    <div key={tradeId} className='rosetta-trade-note-item'>
                      <strong>{trade.trade_name}: </strong>
                      {trade.trade_description}
                    </div>
                  ) : null;
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default ScopeDetailsPanel;
